import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import {
  Alert,
  Box,
  SkeletonLoader,
  Space,
  TextBody,
  TextHeading,
  get,
} from '@ally/metronome-ui'
import styled from 'styled-components'
import allytm from '@ally/allytm'
import { IraAccountView } from './IraAccountView'
import { IraPlan } from '../../types'
import { useIraBeneficiaries } from '../../services/IraBeneficiariesProvider'
import { smoothScrollTo } from '../../utils/smoothScrollto'

const StyledAccountList = styled.ul`
  list-style-type: none;
  padding: 0;
  li {
    margin-bottom: ${get.space('xs')};
    line-height: ${get.space('md')};
    padding-top: ${get.space('xl')};
  }
  li:first-child {
    padding-top: ${get.space('lg')};
  }
`

export const IraBeneficiaries: React.FC<{ headerRef: any }> = ({
  headerRef,
}) => {
  const { iraPlans, plansLoading, hasPlansError } = useIraBeneficiaries()
  const { state: locationState } = useLocation<{ deepLinkId: string }>()

  useEffect(() => {
    if (!plansLoading && locationState && locationState.deepLinkId) {
      const planId = iraPlans.find(iraPlan => {
        return iraPlan.planNumber === locationState.deepLinkId.trim()
      })?.planId

      if (planId) {
        requestAnimationFrame(() => {
          // istanbul ignore next - page scrolling
          smoothScrollTo({
            duration: 2000,
            id: `${planId}-heading`,
          })
          // istanbul ignore next - optional chaining, if planId above covers this
          document.getElementById(`${planId}-heading`)?.focus()
          locationState.deepLinkId = ''
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plansLoading])

  useEffect(() => {
    allytm.event('pageview', 'AOS:Beneficiaries:Bank IRAs')
  }, [])

  return (
    <Box data-testid="ira-content">
      <Space pt="md">
        <TextHeading
          tag="h2"
          size="md"
          weight="bold"
          tabIndex={-1}
          ref={headerRef}
          data-testid="bank-ira-header"
        >
          Bank IRAs
        </TextHeading>
        <TextBody tag="p" size="sm">
          Add up to 6 beneficiaries on each IRA plan. You must designate at
          least one primary beneficiary before you can add contingent
          beneficiaries to your IRA plan.
        </TextBody>
      </Space>
      <>
        {plansLoading ? (
          <Box pt="xl" data-testid="ira-skeleton-loader">
            <SkeletonLoader.Container>
              <SkeletonLoader.Row width="xl" height="l" />
              <SkeletonLoader.Row width="xl" height="l" />
            </SkeletonLoader.Container>
          </Box>
        ) : (
          <>
            {hasPlansError ? (
              <Box mt="md">
                <Alert
                  disableContainer
                  variant="error"
                  body="It's us - we're having a moment. We couldn't load your beneficiaries right now. We'll be quick about it, so check back in a bit."
                  focusOnMount
                />
              </Box>
            ) : (
              <StyledAccountList>
                {
                  /* istanbul ignore next */ iraPlans?.map(
                    (account: IraPlan) => (
                      <li key={account.planId}>
                        <IraAccountView account={account} />
                      </li>
                    ),
                  )
                }
              </StyledAccountList>
            )}
          </>
        )}
      </>
    </Box>
  )
}
